import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';

/**
 * Generates a PDF from the print preview content
 * @param {Object} loanData - The loan data object
 * @param {string} elementId - The ID of the element to capture (optional)
 * @returns {Promise<void>}
 */
export const generatePDF = async (loanData, elementId = 'print-preview-content') => {
  try {
    // Get the element to capture
    const element = document.getElementById(elementId) || document.querySelector('.print-preview-content');
    
    if (!element) {
      // If specific element not found, capture the main report container
      const reportContainer = document.querySelector('.bg-white.rounded-lg, .bg-white');
      if (!reportContainer) {
        throw new Error('Could not find content to export');
      }
      
      // Use the report container instead
      return await captureAndGeneratePDF(reportContainer, loanData);
    }
    
    return await captureAndGeneratePDF(element, loanData);
  } catch (error) {
    console.error('PDF generation failed:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};

/**
 * Captures element and generates PDF with production-ready configuration
 * @param {HTMLElement} element - The element to capture
 * @param {Object} loanData - The loan data object
 * @returns {Promise<void>}
 */
const captureAndGeneratePDF = async (element, loanData) => {
  try {
    // Wait for any pending images or content to load
    await waitForContentToLoad(element);
    
    // Configure html2canvas options for production environment
    const canvasOptions = {
      scale: 3, // High resolution for crisp PDF output
      useCORS: true,
      allowTaint: false, // Prevent tainted canvas issues
      backgroundColor: '#ffffff',
      logging: false,
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      // Production-specific options
      foreignObjectRendering: false, // Disable for better compatibility
      imageTimeout: 15000, // 15 second timeout for images
      removeContainer: true,
      // Handle cross-origin images
      proxy: undefined, // Let browser handle CORS
      onclone: (clonedDoc) => {
        // Remove any problematic elements in the cloned document
        const clonedElement = clonedDoc.querySelector(`#${element.id}`) || clonedDoc.body;
        
        // Remove any external iframes or problematic content
        const iframes = clonedElement.querySelectorAll('iframe');
        iframes.forEach(iframe => iframe.remove());
        
        // Ensure all images have proper src attributes
        const images = clonedElement.querySelectorAll('img');
        images.forEach(img => {
          if (!img.src || img.src.startsWith('blob:')) {
            img.style.display = 'none';
          }
        });
        
        // Fix any relative paths to absolute paths
        const links = clonedElement.querySelectorAll('link[rel="stylesheet"]');
        links.forEach(link => {
          if (link.href && !link.href.startsWith('http')) {
            link.href = new URL(link.href, window.location.origin).href;
          }
        });
        
        return clonedDoc;
      }
    };
    
    // Attempt to capture with retry logic
    const canvas = await captureWithRetry(element, canvasOptions);
    
    // Create PDF document
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true // Enable compression for smaller file size
    });
    
    // Calculate dimensions to fit A4 page
    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // Use PNG for lossless quality (no JPEG blur/artifacts)
    const imgData = canvas.toDataURL('image/png');

    // Handle multi-page content if needed
    if (imgHeight > 297) { // A4 height is 297mm
      await addMultiPageContent(pdf, imgData, imgWidth, imgHeight);
    } else {
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    }
    
    // Generate filename
    const filename = generateFilename(loanData);
    
    // Save the PDF
    pdf.save(filename);
    
  } catch (error) {
    console.error('PDF generation error:', error);
    throw new Error(`PDF generation failed: ${error.message}`);
  }
};

/**
 * Waits for content to load before capturing
 * @param {HTMLElement} element - The element to check
 * @returns {Promise<void>}
 */
const waitForContentToLoad = async (element) => {
  return new Promise((resolve) => {
    const images = element.querySelectorAll('img');
    let loadedCount = 0;
    const totalImages = images.length;
    
    if (totalImages === 0) {
      resolve();
      return;
    }
    
    const checkComplete = () => {
      loadedCount++;
      if (loadedCount >= totalImages) {
        resolve();
      }
    };
    
    images.forEach(img => {
      if (img.complete) {
        checkComplete();
      } else {
        img.addEventListener('load', checkComplete);
        img.addEventListener('error', checkComplete);
      }
    });
    
    // Timeout after 5 seconds
    setTimeout(resolve, 5000);
  });
};

/**
 * Captures element with retry logic for production environments
 * @param {HTMLElement} element - The element to capture
 * @param {Object} options - html2canvas options
 * @returns {Promise<HTMLCanvasElement>}
 */
const captureWithRetry = async (element, options, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`PDF capture attempt ${attempt}/${maxRetries}`);
      
      // Add progressive fallback options for each retry
      const attemptOptions = {
        ...options,
        scale: Math.max(2, options.scale - (attempt - 1)), // Reduce scale on retries but stay sharp
        timeout: Math.max(5000, 15000 - (attempt - 1) * 5000), // Reduce timeout on retries
        useCORS: attempt === 1 ? true : false, // Disable CORS on retries
        allowTaint: attempt > 1 ? true : false, // Allow taint on retries
      };
      
      const canvas = await html2canvas(element, attemptOptions);
      
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        console.log(`PDF capture successful on attempt ${attempt}`);
        return canvas;
      }
      
      throw new Error('Canvas is empty or invalid');
      
    } catch (error) {
      console.warn(`PDF capture attempt ${attempt} failed:`, error);
      lastError = error;
      
      if (attempt < maxRetries) {
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
  }
  
  throw new Error(`PDF capture failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
};

/**
 * Adds multi-page content to PDF
 * @param {jsPDF} pdf - The PDF document
 * @param {string} imgData - The image data
 * @param {number} imgWidth - Image width
 * @param {number} imgHeight - Image height
 * @returns {Promise<void>}
 */
const addMultiPageContent = async (pdf, imgData, imgWidth, imgHeight) => {
  const pageHeight = 297; // A4 height in mm
  let heightLeft = imgHeight;
  let position = 0;
  
  // Add first page
  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  // Add additional pages
  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
};

/**
 * Generates filename from loan description and date
 * @param {Object} loanData - The loan data object
 * @returns {string} - Generated filename
 */
const generateFilename = (loanData) => {
  try {
    // Extract loan description and date
    const description = loanData?.loanDescription || 'Loan Report';
    const loanDate = loanData?.firstPaymentDate || loanData?.loanClosingDate || new Date().toISOString().split('T')[0];
    
    // Format the date
    let formattedDate;
    try {
      const date = new Date(loanDate);
      formattedDate = format(date, 'yyyy-MM-dd');
    } catch (dateError) {
      formattedDate = new Date().toISOString().split('T')[0];
    }
    
    // Sanitize description for filename
    const sanitizedDescription = description
      .replace(/[^a-zA-Z0-9\s-_]/g, '') // Remove invalid characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 50); // Limit length
    
    // Combine description and date
    const filename = `${sanitizedDescription}_${formattedDate}.pdf`;
    
    return filename;
  } catch (error) {
    console.error('Error generating filename:', error);
    const fallbackDate = new Date().toISOString().split('T')[0];
    return `Loan_Report_${fallbackDate}.pdf`;
  }
};

/**
 * Validates if required data is available for PDF generation
 * @param {Object} loanData - The loan data object
 * @returns {boolean} - True if data is valid
 */
export const validatePDFData = (loanData) => {
  if (!loanData) return false;
  
  // Check if essential loan data exists
  const hasLoanAmount = loanData.loanAmount || loanData.principal;
  const hasPaymentData = loanData.monthlyPayment || loanData.paymentAmount;
  
  return hasLoanAmount && hasPaymentData;
};

/**
 * Detects if running in production environment
 * @returns {boolean} - True if in production
 */
export const isProductionEnvironment = () => {
  return process.env.NODE_ENV === 'production' || 
         window.location.protocol === 'https:' || 
         !window.location.hostname.includes('localhost');
};

export default { generatePDF, validatePDFData, isProductionEnvironment };